import './style.css';
import './components/headerBar.js';
import './components/createNoteForm.js';
import './components/noteCard.js';
import './components/loadingIndicator.js';
import {
  getAllNotes,
  createNote,
  deleteNote,
  archiveNote,
  unarchiveNote,
  getArchivedNotes,
  getNote,
} from './api/notesApi.js';
import Swal from 'sweetalert2';
import anime from 'animejs/lib/anime.es.js';

function getColor() {
  return '#d6e6b4';
}

function showLoading() {
  const el = document.getElementById('appLoading');
  if (el) el.style.display = 'block';
}

function hideLoading() {
  const el = document.getElementById('appLoading');
  if (el) el.style.display = 'none';
}

let currentView = 'list';

function renderNotes() {
  const notesList = document.getElementById('notesList');
  notesList.innerHTML = '';

  const data = globalThis.notesListData || [];
  for (const note of data) {
    const noteElement = document.createElement('note-card');
    noteElement.setAttribute('title', note.title);
    noteElement.setAttribute('body', note.body);
    noteElement.setAttribute('date', note.createdAt);
    noteElement.setAttribute('color', getColor());
    noteElement.setAttribute('note-id', note.id);
    noteElement.setAttribute('archived', note.archived ? 'true' : 'false');
    notesList.appendChild(noteElement);
  // prepare for entrance animation
  noteElement.style.opacity = '0';
  noteElement.style.transform = 'translateY(12px)';
  noteElement.style.willChange = 'transform, opacity';

    noteElement.addEventListener('click', async () => {
      showLoading();
      try {
        const detail = await getNote(note.id);
        hideLoading();
        const created = new Date(detail.createdAt).toLocaleDateString('id-ID', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
        const isArchived = !!detail.archived;
        const result = await Swal.fire({
          title: `
          <div class="title-note" style="text-align:left;white-space:pre-line">
            <h2 style="font-size: 20px;">${detail.title || 'Note detail'}</h2>
            <p class="note-date" style="font-size: 14px; color: #666;">${created}</p>
          </div>`,
          html: `
          <div style="text-align:left">
            <div class="swal-note-body" style="margin-top:8px;white-space:pre-wrap;color:#333">${detail.body}</div>
          </div>`,
          showCancelButton: true,
          showDenyButton: true,
          denyButtonText: isArchived ? 'Unarchive' : 'Archive',
          confirmButtonText: 'Delete',
          cancelButtonText: 'Close',
          focusCancel: true,
          heightAuto: false,
          didOpen: () => {
            const swalTitle = document.querySelector('.swal2-title');
            if (swalTitle) {
              swalTitle.innerHTML = swalTitle.innerHTML.replace(/<br\s*\/?/gi, '');
            }
          },
        });

        if (result.isConfirmed) {
          showLoading();
          try {
            await deleteNote(detail.id);
            await loadNotes();
            hideLoading();
            await Swal.fire({ icon: 'success', title: 'Deleted', text: 'Note has been deleted.' });
          } catch (err) {
            hideLoading();
            await Swal.fire({ icon: 'error', title: 'Delete failed', text: err.message });
          } finally {
            hideLoading();
          }
        } else if (result.isDenied) {
          showLoading();
          try {
            if (isArchived) {
              await unarchiveNote(detail.id);
            } else {
              await archiveNote(detail.id);
            }
            await loadNotes();
            hideLoading();
            await Swal.fire({
              icon: 'success',
              title: 'Success',
              text: isArchived ? 'Note restored.' : 'Note archived.',
            });
          } catch (err) {
            hideLoading();
            await Swal.fire({ icon: 'error', title: 'Operation failed', text: err.message });
          } finally {
            hideLoading();
          }
        }
      } catch (err) {
        hideLoading();
        await Swal.fire({ icon: 'error', title: 'Failed to load note', text: err.message });
      } finally {
        hideLoading();
      }
    });
  }

  try {
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const nodes = Array.from(document.querySelectorAll('#notesList > note-card'));
    if (!reduce && nodes.length > 0) {
      anime({
        targets: nodes,
        translateY: [12, 0],
        opacity: [0, 1],
        easing: 'cubicBezier(.22,.8,.36,1)',
        duration: 420,
        delay: anime.stagger(80),
      });
    } else if (nodes.length > 0) {
      nodes.forEach((n) => {
        n.style.opacity = '1';
        n.style.transform = 'none';
      });
    }
  } catch (err) {
    console.error('animation error', err);
  }

  if (!data || data.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'empty-list';
    if (currentView === 'archived') {
      empty.innerHTML = '<p>No archived notes. Archive notes to see them here.</p>';
    } else {
      empty.innerHTML =
        '<p>No notes yet. Save a new note by pressing the <strong>New</strong> button.</p>';
    }
    notesList.appendChild(empty);
  }
}

async function loadNotes() {
  showLoading();
  try {
    let notes;
    if (currentView === 'archived') {
      notes = await getArchivedNotes();
    } else {
      notes = await getAllNotes();
    }

    if (notes?.notes) {
      globalThis.notesListData = notes.notes;
    } else {
      globalThis.notesListData = Array.isArray(notes) ? notes : [];
    }
    const heading = document.querySelector('main h2');
    if (heading) heading.innerText = currentView === 'archived' ? 'Archived Notes' : 'List Notes';
    if (currentView === 'archived') {
      document.body.classList.remove('show-create');
      const strip = document.querySelector('.new-strip');
      if (strip) strip.classList.remove('active');
    }
    renderNotes();
  } catch (err) {
    hideLoading();
    await Swal.fire({
      icon: 'error',
      title: 'Failed to load notes',
      text: err.message,
    });
  } finally {
    hideLoading();
  }
}

async function addNoteHandler(event) {
  event.preventDefault();
  const root = this.shadowRoot || this;
  const titleEl = root.querySelector('#noteTitleNew');
  const bodyEl = root.querySelector('#noteBodyNew');
  const title = titleEl ? titleEl.value : '';
  const body = bodyEl ? bodyEl.value : '';

  if (!title || !body || title.length < 3 || body.length < 10) return;

  const newNote = {
    title,
    body,
  };

  showLoading();
  try {
    await createNote(newNote);
    await loadNotes();
  const formRoot = this.shadowRoot || this;
  const formEl = formRoot.querySelector('#createNoteForm');
  if (formEl) formEl.reset();
  if (typeof this.checkFormValidity === 'function') this.checkFormValidity();

    document.body.classList.remove('show-create');
    const strip = document.querySelector('.new-strip');
    if (strip) {
      strip.classList.remove('active');
    }

    currentView = 'list';
    const listMenuAfter = document.querySelector('.list-strip');
    if (listMenuAfter) {
      listMenuAfter.classList.add('active');
    }

    const notesListEl = document.querySelector('.notes-list');
    if (notesListEl) {
      notesListEl.style.display = '';
    }

    hideLoading();
    await Swal.fire({ icon: 'success', title: 'Success', text: 'Note created successfully.' });
  } catch (err) {
    hideLoading();
    await Swal.fire({ icon: 'error', title: 'Failed to create note', text: err.message });
  } finally {
    hideLoading();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  loadNotes();

  const noteForm = document.querySelector('create-note-form');
  if (noteForm) {
    const form = (noteForm.shadowRoot && noteForm.shadowRoot.getElementById('createNoteForm')) 
      || noteForm.querySelector('#createNoteForm');
    if (form) form.addEventListener('submit', addNoteHandler.bind(noteForm));
  }

  const listStripInit = document.querySelector('.list-strip');
  const archiveInit = document.querySelector('.archive-strip');
  const newInit = document.querySelector('.new-strip');
  if (listStripInit) listStripInit.classList.add('active');
  if (archiveInit) archiveInit.classList.remove('active');
  if (newInit) newInit.classList.remove('active');
  document.body.classList.remove('show-create');
  const notesListInit = document.querySelector('.notes-list');
  if (notesListInit) notesListInit.style.display = '';

  const sidebarNew = document.querySelector('.new-strip');
  if (sidebarNew) {
    sidebarNew.addEventListener('click', () => {
      const isActive = sidebarNew.classList.toggle('active');
      currentView = 'new';
      const archiveMenu = document.querySelector('.archive-strip');
      if (archiveMenu) archiveMenu.classList.remove('active');
      const listMenu = document.querySelector('.list-strip');
      if (listMenu) listMenu.classList.remove('active');
      if (isActive) {
        document.body.classList.add('show-create');
        const notesListEl = document.querySelector('.notes-list');
        if (notesListEl) notesListEl.style.display = 'none';
        const nf = document.querySelector('create-note-form');
        if (nf) {
          const focusEl = (nf.shadowRoot && nf.shadowRoot.getElementById('noteTitleNew')) 
            || nf.querySelector('#noteTitleNew');
          if (focusEl) focusEl.focus();
        }
      } else {
        document.body.classList.remove('show-create');
        const notesListEl = document.querySelector('.notes-list');
        if (notesListEl) notesListEl.style.display = '';
        currentView = 'list';
        const listMenu2 = document.querySelector('.list-strip');
        if (listMenu2) listMenu2.classList.add('active');

        const nfClose = document.querySelector('create-note-form');
        if (nfClose) {
          const formRootClose = (nfClose.shadowRoot) ? nfClose.shadowRoot : nfClose;
          const formElClose = formRootClose.querySelector('#createNoteForm');
          if (formElClose) formElClose.reset();
          if (typeof nfClose.checkFormValidity === 'function') nfClose.checkFormValidity();
        }
        loadNotes();
      }
    });
  }

  const listMenu = document.querySelector('.list-strip');
  if (listMenu) {
    listMenu.addEventListener('click', async () => {
      currentView = 'list';
      document.body.classList.remove('show-create');
      const notesListEl = document.querySelector('.notes-list');
      if (notesListEl) notesListEl.style.display = '';
      listMenu.classList.add('active');
      const newStrip = document.querySelector('.new-strip');
      if (newStrip) newStrip.classList.remove('active');
      const archiveMenu2 = document.querySelector('.archive-strip');
      if (archiveMenu2) archiveMenu2.classList.remove('active');
      await loadNotes();
    });
  }

  const archiveMenu = document.querySelector('.archive-strip');
  if (archiveMenu) {
    archiveMenu.addEventListener('click', async () => {
      currentView = 'archived';
      document.body.classList.remove('show-create');
      const notesListEl = document.querySelector('.notes-list');
      if (notesListEl) notesListEl.style.display = '';
      await loadNotes();
      archiveMenu.classList.add('active');
      const newStrip = document.querySelector('.new-strip');
      if (newStrip) newStrip.classList.remove('active');
      const listStrip = document.querySelector('.list-strip');
      if (listStrip) listStrip.classList.remove('active');
    });
  }

  document.addEventListener('delete-note', async (e) => {
    const { id } = e.detail || {};
    if (!id) return;
    const result = await Swal.fire({
      title: 'Delete this note?',
      text: "This action can't be undone.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    showLoading();
    try {
      await deleteNote(id);
      await loadNotes();
      hideLoading();
      await Swal.fire({ icon: 'success', title: 'Success', text: 'Note deleted.' });
    } catch (err) {
      hideLoading();
      await Swal.fire({ icon: 'error', title: 'Failed to delete note', text: err.message });
    } finally {
      hideLoading();
    }
  });

  document.addEventListener('toggle-archive', async (e) => {
    const { id, archived } = e.detail || {};
    if (!id) return;
    showLoading();
    try {
      if (archived) {
        await archiveNote(id);
      } else {
        await unarchiveNote(id);
      }
      await loadNotes();
      hideLoading();
      await Swal.fire({
        icon: 'success',
        title: 'Success',
        text: archived ? 'Note archived.' : 'Note restored.',
      });
    } catch (err) {
      hideLoading();
      await Swal.fire({
        icon: 'error',
        title: 'Failed to change archive status',
        text: err.message,
      });
    } finally {
      hideLoading();
    }
  });
});
